import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="kyc-phone-validation"
export default class extends Controller {
  static targets = ["errorMessage"]
  
  connect() {
    // Find the search input and button in FilterComponent
    this.searchInput = this.element.querySelector('input[name="search"]')
    this.searchButton = this.element.querySelector('button[type="submit"]')
    
    if (this.searchInput) {
      // Add validation listeners
      this.searchInput.addEventListener('input', (e) => this.validatePhone(e))
      this.searchInput.addEventListener('blur', (e) => this.validatePhone(e))
      
      // Create validation icon container
      this.createValidationIcon()
      
      // Validate on page load if there's a value
      if (this.searchInput.value) {
        this.validatePhone({ target: this.searchInput })
      }
    }
  }
  
  disconnect() {
    if (this.searchInput) {
      this.searchInput.removeEventListener('input', this.validatePhone)
      this.searchInput.removeEventListener('blur-sm', this.validatePhone)
    }
  }
  
  createValidationIcon() {
    // Create icon container next to search input
    const inputWrapper = this.searchInput.parentElement
    inputWrapper.style.position = 'relative'
    
    this.validationIcon = document.createElement('div')
    this.validationIcon.className = 'absolute right-12 top-1/2 transform -translate-y-1/2 z-10'
    inputWrapper.appendChild(this.validationIcon)
    
    // Create error message container below the input
    this.errorContainer = document.createElement('div')
    this.errorContainer.className = 'absolute left-0 right-0 top-full mt-1 z-20'
    inputWrapper.appendChild(this.errorContainer)
  }

  validatePhone(event) {
    const input = event.target
    const phoneNumber = input.value.trim()
    
    // Clear previous validation
    this.clearValidation(input)
    
    // Allow empty search (to browse all records)
    if (!phoneNumber) {
      this.enableButton(this.searchButton)
      return true
    }
    
    // Remove any non-digit characters for validation
    const digitsOnly = phoneNumber.replace(/\D/g, '')
    
    // Check for spaces in the input
    if (phoneNumber.includes(' ')) {
      this.setError(input, "Phone number should not contain spaces")
      this.disableButton(this.searchButton)
      return false
    }
    
    // Check for special characters except + at the beginning
    if (!/^[\+]?[0-9]+$/.test(phoneNumber)) {
      this.setError(input, "Phone number should only contain digits")
      this.disableButton(this.searchButton)
      return false
    }
    
    let isValid = false
    let errorMessage = ""
    
    // Uganda international format (256...)
    if (phoneNumber.startsWith('256') || phoneNumber.startsWith('+256')) {
      const expectedLength = phoneNumber.startsWith('+256') ? 13 : 12
      const actualLength = phoneNumber.length
      
      if (actualLength !== expectedLength) {
        errorMessage = `Uganda number (256) must be ${expectedLength} digits (currently ${actualLength})`
      } else {
        // Validate Uganda number format (256 7XX XXX XXX)
        const ugandaPattern = phoneNumber.startsWith('+256') 
          ? /^\+256[37]\d{8}$/ 
          : /^256[37]\d{8}$/
          
        if (!ugandaPattern.test(phoneNumber)) {
          errorMessage = "Invalid Uganda number format (should be 256 7XX XXX XXX or 256 3XX XXX XXX)"
        } else {
          isValid = true
        }
      }
    }
    // Uganda local format (07... or 03...)
    else if (phoneNumber.startsWith('07') || phoneNumber.startsWith('03')) {
      if (phoneNumber.length !== 10) {
        errorMessage = `Local Uganda number must be 10 digits (currently ${phoneNumber.length})`
      } else {
        isValid = true
      }
    }
    // Other formats starting with 0
    else if (phoneNumber.startsWith('0')) {
      if (phoneNumber.length > 10) {
        errorMessage = `Numbers starting with 0 must be maximum 10 digits (currently ${phoneNumber.length})`
      } else if (phoneNumber.length < 9) {
        errorMessage = `Phone number too short (minimum 9 digits, currently ${phoneNumber.length})`
      } else {
        isValid = true
      }
    }
    // International format with +
    else if (phoneNumber.startsWith('+')) {
      if (phoneNumber.length < 10 || phoneNumber.length > 15) {
        errorMessage = `International number must be between 10-15 digits (currently ${phoneNumber.length})`
      } else {
        isValid = true
      }
    }
    // Other international formats without +
    else {
      if (digitsOnly.length < 7 || digitsOnly.length > 15) {
        errorMessage = `Phone number must be between 7-15 digits (currently ${digitsOnly.length})`
      } else {
        isValid = true
      }
    }
    
    if (isValid) {
      this.setSuccess(input)
      this.enableButton(this.searchButton)
      return true
    } else {
      this.setError(input, errorMessage)
      this.disableButton(this.searchButton)
      return false
    }
  }
  
  clearValidation(input) {
    input.classList.remove('border-red-500', 'border-green-500', 'bg-red-50', 'bg-green-50')
    input.classList.add('border-gray-300')
    
    if (this.errorContainer) {
      this.errorContainer.innerHTML = ''
    }
    
    if (this.validationIcon) {
      this.validationIcon.innerHTML = ''
    }
  }
  
  setError(input, message) {
    input.classList.remove('border-gray-300', 'border-green-500', 'bg-green-50')
    input.classList.add('border-red-500', 'bg-red-50')
    
    if (this.errorContainer) {
      this.errorContainer.innerHTML = `
        <div class="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm flex items-start gap-2 shadow-xs">
          <svg class="h-4 w-4 text-red-400 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <span>${message}</span>
        </div>
      `
    }
    
    if (this.validationIcon) {
      this.validationIcon.innerHTML = `
        <svg class="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
      `
    }
  }
  
  setSuccess(input) {
    input.classList.remove('border-gray-300', 'border-red-500', 'bg-red-50')
    input.classList.add('border-green-500', 'bg-green-50')
    
    if (this.errorContainer) {
      this.errorContainer.innerHTML = ''
    }
    
    if (this.validationIcon) {
      this.validationIcon.innerHTML = `
        <svg class="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
      `
    }
  }
  
  disableButton(button) {
    if (button) {
      button.disabled = true
      button.classList.add('opacity-50', 'cursor-not-allowed')
      button.classList.remove('hover:bg-blue-700')
      button.title = 'Fix phone number format before searching'
    }
  }
  
  enableButton(button) {
    if (button) {
      button.disabled = false
      button.classList.remove('opacity-50', 'cursor-not-allowed')
      button.classList.add('hover:bg-blue-700')
      button.title = 'Search KYC records'
    }
  }
  
}
